// frontend/src/components/modal/SupplierInvoiceModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  X, FileText, Plus, Trash2, Upload, Eye, XCircle,
  ChevronDown, ChevronUp, DollarSign, Tag
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const Section = ({ title, icon: Icon, color, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-700 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-900/60 hover:bg-gray-900/80 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon size={15} className={`text-${color}-400`} />
          <span className="text-white text-sm font-medium">{title}</span>
        </div>
        {open ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
      </button>
      {open && <div className="p-4 bg-gray-800/30 space-y-3">{children}</div>}
    </div>
  );
};

const Field = ({ label, name, value, onChange, type = 'text', placeholder = '', required = false }) => (
  <div>
    <label className="block text-xs text-gray-400 mb-1">
      {label}{required && <span className="text-red-400 ml-1">*</span>}
    </label>
    <input
      type={type} name={name} value={value || ''}
      onChange={onChange} placeholder={placeholder} required={required}
      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
    />
  </div>
);

const emptyLine = () => ({
  _key:        Math.random().toString(36).slice(2),
  description: '',
  quantity:    1,
  unit:        '',
  unit_price:  0,
});

const SupplierInvoiceModal = ({ invoice, suppliers, onClose }) => {
  const isEdit = !!invoice;
  const fileInputRef = useRef();

  const emptyForm = {
    supplier_id:      '',
    invoice_date:     new Date().toISOString().split('T')[0],
    due_date:         '',
    received_date:    new Date().toISOString().split('T')[0],
    invoice_type:     'purchase',
    supplier_ref:     '',
    tax_state_rate:   '',
    tax_airport_rate: '',
    tax_airport_label:'Airport Fee',
    tax_other_amount: 0,
    tax_other_label:  '',
    notes:            '',
    status:           'received',
  };

  const [form,         setForm]         = useState(emptyForm);
  const [lines,        setLines]        = useState([emptyLine()]);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');
  const [companyTaxes, setCompanyTaxes] = useState({ tax_rate: 0.0825, airport_tax_rate: 0, airport_tax_label: 'Airport Fee' });
  const [pdfFile,      setPdfFile]      = useState(null);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [existingPdf,  setExistingPdf]  = useState(null);

  // Charger taxes depuis company_settings
  useEffect(() => {
    axios.get(`${API_BASE}/company/settings`)
      .then(r => {
        if (r.data) setCompanyTaxes({
          tax_rate:          r.data.tax_rate          || 0.0825,
          airport_tax_rate:  r.data.airport_tax_rate  || 0,
          airport_tax_label: r.data.airport_tax_label || 'Airport Fee',
        });
      })
      .catch(() => {});
  }, []);

  // Pré-remplir en mode édition
  useEffect(() => {
    if (invoice) {
      const f = { ...emptyForm, ...invoice };
      if (f.invoice_date)   f.invoice_date   = f.invoice_date.split('T')[0];
      if (f.due_date)       f.due_date       = f.due_date.split('T')[0];
      if (f.received_date)  f.received_date  = f.received_date.split('T')[0];
      f.tax_state_rate   = f.tax_state_rate   != null ? parseFloat(f.tax_state_rate)   : '';
      f.tax_airport_rate = f.tax_airport_rate != null ? parseFloat(f.tax_airport_rate) : '';
      setForm(f);

      if (invoice.attachment_path) setExistingPdf(invoice.attachment_filename || 'invoice.pdf');

      // Charger les lignes
      if (invoice.id) {
        axios.get(`${API_BASE}/supplier-invoices/${invoice.id}`)
          .then(r => {
            if (r.data.lines?.length) {
              setLines(r.data.lines.map(l => ({ ...l, _key: l.id })));
            }
          })
          .catch(() => {});
      }
    }
  }, [invoice]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // ── Lignes ──────────────────────────────────────────────────────────────
  const addLine = () => setLines(prev => [...prev, emptyLine()]);

  const removeLine = (key) => {
    if (lines.length === 1) return;
    setLines(prev => prev.filter(l => l._key !== key));
  };

  const updateLine = (key, field, value) => {
    setLines(prev => prev.map(l =>
      l._key === key ? { ...l, [field]: value } : l
    ));
  };

  // ── Calculs ─────────────────────────────────────────────────────────────
  const subtotal = lines.reduce((s, l) => s + (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0), 0);
  const taxStateRate   = parseFloat(form.tax_state_rate   !== '' ? form.tax_state_rate   : companyTaxes.tax_rate)          || 0;
  const taxAirportRate = parseFloat(form.tax_airport_rate !== '' ? form.tax_airport_rate : companyTaxes.airport_tax_rate)  || 0;
  const taxStateAmt    = subtotal * taxStateRate;
  const taxAirportAmt  = subtotal * taxAirportRate;
  const taxOther       = parseFloat(form.tax_other_amount) || 0;
  const total          = subtotal + taxStateAmt + taxAirportAmt + taxOther;

  const fmt2 = (v) => `$${v.toFixed(2)}`;

  // ── PDF ──────────────────────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.type !== 'application/pdf') { alert('Only PDF files are accepted.'); return; }
    if (f.size > 10 * 1024 * 1024)    { alert('File too large. Max 10 MB.');    return; }
    setPdfFile(f);
  };

  const uploadPdf = async (invoiceId) => {
    if (!pdfFile) return;
    setPdfUploading(true);
    const fd = new FormData();
    fd.append('file', pdfFile);
    try {
      await axios.post(`${API_BASE}/supplier-invoices/${invoiceId}/upload-pdf`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (e) {
      console.error('PDF upload error:', e);
    } finally {
      setPdfUploading(false);
    }
  };

  const handleDeleteExistingPdf = async () => {
    if (!invoice?.id) return;
    if (!window.confirm('Remove the attached PDF?')) return;
    try {
      await axios.delete(`${API_BASE}/supplier-invoices/${invoice.id}/pdf`);
      setExistingPdf(null);
    } catch (e) {
      alert('Error removing PDF');
    }
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.supplier_id) { setError('Please select a supplier.'); return; }
    if (!lines.some(l => l.description.trim())) { setError('At least one line item is required.'); return; }

    setSaving(true);
    setError('');

    const payload = {
      ...form,
      lines: lines
        .filter(l => l.description.trim())
        .map((l, i) => ({
          description: l.description,
          quantity:    parseFloat(l.quantity)   || 1,
          unit:        l.unit || null,
          unit_price:  parseFloat(l.unit_price) || 0,
          line_order:  i + 1,
        })),
      tax_state_rate:   form.tax_state_rate   !== '' ? parseFloat(form.tax_state_rate)   : null,
      tax_airport_rate: form.tax_airport_rate !== '' ? parseFloat(form.tax_airport_rate) : null,
      tax_other_amount: parseFloat(form.tax_other_amount) || 0,
    };

    if (!payload.due_date)      delete payload.due_date;
    if (!payload.tax_other_label) delete payload.tax_other_label;

    try {
      let savedId;
      if (isEdit) {
        await axios.put(`${API_BASE}/supplier-invoices/${invoice.id}`, payload);
        savedId = invoice.id;
      } else {
        const res = await axios.post(`${API_BASE}/supplier-invoices/`, payload);
        savedId = res.data.id;
      }

      // Upload PDF si sélectionné
      if (pdfFile && savedId) await uploadPdf(savedId);

      onClose(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error saving invoice');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-500/20 rounded-xl">
              <FileText size={18} className="text-indigo-400" />
            </div>
            <div>
              <h2 className="text-white font-semibold">
                {isEdit ? `Edit — ${invoice.invoice_number}` : 'New Supplier Invoice'}
              </h2>
              <p className="text-gray-400 text-xs mt-0.5">Accounts Payable</p>
            </div>
          </div>
          <button onClick={() => onClose(false)} className="text-gray-400 hover:text-white p-1 rounded-lg transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">

          {error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-lg px-4 py-3 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* ── Header info ── */}
          <Section title="Invoice Information" icon={FileText} color="indigo" defaultOpen>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">

              {/* Supplier */}
              <div className="sm:col-span-2 lg:col-span-1">
                <label className="block text-xs text-gray-400 mb-1">Supplier <span className="text-red-400">*</span></label>
                <select
                  name="supplier_id"
                  value={form.supplier_id}
                  onChange={handleChange}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
                >
                  <option value="">— Select supplier —</option>
                  {suppliers.map(s => (
                    <option key={s.id} value={s.id}>{s.company_name}</option>
                  ))}
                </select>
              </div>

              {/* Type */}
              <div>
                <label className="block text-xs text-gray-400 mb-1">Type</label>
                <select
                  name="invoice_type"
                  value={form.invoice_type}
                  onChange={handleChange}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
                >
                  <option value="purchase">Purchase</option>
                  <option value="recurring_charge">Recurring Charge</option>
                </select>
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs text-gray-400 mb-1">Status</label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
                >
                  <option value="received">Received</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="disputed">Disputed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <Field label="Invoice Date"     name="invoice_date"   value={form.invoice_date}   onChange={handleChange} type="date" required />
              <Field label="Due Date"         name="due_date"       value={form.due_date}       onChange={handleChange} type="date" />
              <Field label="Received Date"    name="received_date"  value={form.received_date}  onChange={handleChange} type="date" />
              <Field label="Supplier Ref #"   name="supplier_ref"   value={form.supplier_ref}   onChange={handleChange} placeholder="Their invoice number" />
            </div>
          </Section>

          {/* ── Line items ── */}
          <Section title="Line Items" icon={Tag} color="emerald" defaultOpen>
            <div className="space-y-2">
              {/* Header row */}
              <div className="hidden sm:grid grid-cols-12 gap-2 text-xs text-gray-400 px-1">
                <span className="col-span-5">Description</span>
                <span className="col-span-2 text-center">Qty</span>
                <span className="col-span-1 text-center">Unit</span>
                <span className="col-span-3 text-right">Unit Price</span>
                <span className="col-span-1 text-right">Total</span>
              </div>

              {lines.map(line => (
                <div key={line._key} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-12 sm:col-span-5">
                    <input
                      type="text"
                      value={line.description}
                      onChange={e => updateLine(line._key, 'description', e.target.value)}
                      placeholder="Description"
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <input
                      type="number"
                      value={line.quantity}
                      onChange={e => updateLine(line._key, 'quantity', e.target.value)}
                      min="0" step="0.001"
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white text-center focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                  <div className="col-span-3 sm:col-span-1">
                    <input
                      type="text"
                      value={line.unit}
                      onChange={e => updateLine(line._key, 'unit', e.target.value)}
                      placeholder="pcs"
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-2 py-1.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-3">
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">$</span>
                      <input
                        type="number"
                        value={line.unit_price}
                        onChange={e => updateLine(line._key, 'unit_price', e.target.value)}
                        min="0" step="0.01"
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-5 pr-3 py-1.5 text-sm text-white text-right focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                  </div>
                  <div className="col-span-1 flex items-center justify-end gap-1">
                    <span className="text-gray-300 text-xs hidden sm:block">
                      {fmt2((parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price) || 0))}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeLine(line._key)}
                      disabled={lines.length === 1}
                      className="p-1 text-gray-600 hover:text-red-400 disabled:opacity-30 transition-colors"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addLine}
                className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-sm mt-2 transition-colors"
              >
                <Plus size={14} /> Add line
              </button>
            </div>
          </Section>

          {/* ── Taxes ── */}
          <Section title="Taxes & Totals" icon={DollarSign} color="yellow">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  State Tax Rate
                  <span className="text-gray-500 ml-1">
                    (default: {(companyTaxes.tax_rate * 100).toFixed(2)}%)
                  </span>
                </label>
                <div className="relative">
                  <input
                    type="number" name="tax_state_rate"
                    value={form.tax_state_rate}
                    onChange={handleChange}
                    step="0.0001" min="0" max="1"
                    placeholder={String(companyTaxes.tax_rate)}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">0–1</span>
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1">
                  {form.tax_airport_label || companyTaxes.airport_tax_label} Rate
                  <span className="text-gray-500 ml-1">
                    (default: {(companyTaxes.airport_tax_rate * 100).toFixed(2)}%)
                  </span>
                </label>
                <input
                  type="number" name="tax_airport_rate"
                  value={form.tax_airport_rate}
                  onChange={handleChange}
                  step="0.0001" min="0" max="1"
                  placeholder={String(companyTaxes.airport_tax_rate)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
                />
              </div>

              <Field label="Airport Fee Label"  name="tax_airport_label"  value={form.tax_airport_label}  onChange={handleChange} placeholder="Airport Fee" />
              <Field label="Other Tax ($)"       name="tax_other_amount"   value={form.tax_other_amount}   onChange={handleChange} type="number" />
              <Field label="Other Tax Label"     name="tax_other_label"    value={form.tax_other_label}    onChange={handleChange} placeholder="e.g. City Tax" />
            </div>

            {/* Summary */}
            <div className="mt-4 bg-gray-900/60 rounded-xl p-4 space-y-2 border border-gray-700/50">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Subtotal</span>
                <span className="text-white">{fmt2(subtotal)}</span>
              </div>
              <div className="flex justify-between text-xs text-gray-400">
                <span>State Tax ({(taxStateRate * 100).toFixed(2)}%)</span>
                <span>{fmt2(taxStateAmt)}</span>
              </div>
              {taxAirportRate > 0 && (
                <div className="flex justify-between text-xs text-gray-400">
                  <span>{form.tax_airport_label || companyTaxes.airport_tax_label} ({(taxAirportRate * 100).toFixed(2)}%)</span>
                  <span>{fmt2(taxAirportAmt)}</span>
                </div>
              )}
              {taxOther > 0 && (
                <div className="flex justify-between text-xs text-gray-400">
                  <span>{form.tax_other_label || 'Other Tax'}</span>
                  <span>{fmt2(taxOther)}</span>
                </div>
              )}
              <div className="flex justify-between text-base font-bold border-t border-gray-700 pt-2">
                <span className="text-white">Total</span>
                <span className="text-indigo-400">{fmt2(total)}</span>
              </div>
            </div>
          </Section>

          {/* ── PDF ── */}
          <Section title="Attach Invoice PDF" icon={Upload} color="blue" defaultOpen={false}>
            <div className="space-y-3">

              {/* Existing PDF */}
              {existingPdf && (
                <div className="flex items-center justify-between bg-gray-900/60 border border-gray-700 rounded-lg px-4 py-2">
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <FileText size={14} className="text-indigo-400" />
                    {existingPdf}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => window.open(`${API_BASE}/supplier-invoices/${invoice?.id}/pdf`, '_blank')}
                      className="p-1.5 text-gray-400 hover:text-white transition-colors"
                      title="View PDF"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={handleDeleteExistingPdf}
                      className="p-1.5 text-gray-400 hover:text-red-400 transition-colors"
                      title="Remove PDF"
                    >
                      <XCircle size={14} />
                    </button>
                  </div>
                </div>
              )}

              {/* Upload area */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-700 hover:border-indigo-500 rounded-xl p-6 text-center cursor-pointer transition-colors"
              >
                <Upload size={20} className="mx-auto mb-2 text-gray-500" />
                <p className="text-gray-400 text-sm">
                  {pdfFile ? pdfFile.name : (existingPdf ? 'Replace PDF' : 'Click to upload PDF')}
                </p>
                <p className="text-gray-600 text-xs mt-1">PDF only · Max 10 MB</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={handleFileSelect}
                />
              </div>

              {pdfFile && (
                <div className="flex items-center justify-between bg-indigo-500/10 border border-indigo-500/30 rounded-lg px-4 py-2">
                  <div className="flex items-center gap-2 text-sm text-indigo-300">
                    <FileText size={14} />
                    {pdfFile.name} ({(pdfFile.size / 1024).toFixed(0)} KB)
                  </div>
                  <button type="button" onClick={() => setPdfFile(null)} className="text-gray-400 hover:text-red-400 transition-colors">
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>
          </Section>

          {/* ── Notes ── */}
          <Section title="Notes" icon={FileText} color="gray" defaultOpen={false}>
            <textarea
              name="notes"
              value={form.notes || ''}
              onChange={handleChange}
              rows={3}
              placeholder="Internal notes..."
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 resize-none"
            />
          </Section>

        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-700 flex-shrink-0">
          <button
            type="button"
            onClick={() => onClose(false)}
            className="px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || pdfUploading}
            className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 rounded-lg text-white text-sm font-medium transition-colors"
          >
            {saving || pdfUploading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupplierInvoiceModal;